import React from 'react';
import { Check, RefreshCw, Loader2 } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import type { ShotDraft } from '../../services/studioService';
import AutoTextarea from '../ui/AutoTextarea';
import Tooltip from '../ui/Tooltip';

/**
 * THE SCRIPT — generated in place, then edited or re-run. One document, not two columns.
 *
 * It used to be the right two-thirds of a split page: the creator's idea in a narrow left column, the writer's
 * output in a tall card beside it. Owner 2026-08-23: *"instead of two text areas side by side, borrow how the
 * industrial approach is these days — like Jira ticket writing, there is an AI spark button that triggers
 * generative writing, and the user can regenerate or edit."* That pattern is right for this stage: the script
 * is not a separate artifact sitting next to the idea, it is what the idea BECOMES. So the page reads top to
 * bottom — idea, spark, script — and the script arrives where you were already looking.
 *
 * Three consequences, all deliberate:
 *   - **Nothing renders before the first pass.** The 420px empty card that used to sit there taught nothing the
 *     button above it did not already say, and it made an unstarted page look broken.
 *   - **Every beat is editable, in both states.** A generated draft you cannot touch is a slot machine. Editing
 *     an APPROVED beat edits the shot itself, because the approved script IS the shot list projected back.
 *   - **Regenerate sits with the text it replaces**, not in a toolbar three inches away.
 *
 * Presentational only: beats and verbs arrive as props.
 */
export interface ScriptPreviewProps {
  /** The beats to show — a live proposal, or the approved script read back off the production. */
  draft: ShotDraft[] | null;
  /** True when `draft` is the APPROVED script (a projection of the saved shots) rather than a live proposal. */
  approved?: boolean;
  drafting?: boolean;
  onApprove: () => void;
  onRegenerate: () => void;
  /** Rewrite one beat in place. Omitted → the beats render read-only. */
  onEditBeat?: (index: number, line: string) => void;
}


/** Plain words for the stored vocabulary — a creator reads "Close", never "CU". */
const SIZE_WORDS: Record<string, string> = {
  ECU: 'Extreme close', CU: 'Close', MCU: 'Chest up', MS: 'Waist up',
  MLS: 'Full body', WS: 'Wide', EWS: 'Very wide',
};
const MOVE_WORDS: Record<string, string> = {
  static: 'Locked off', push: 'Push in', pull: 'Pull out', pan: 'Pan',
  tilt: 'Tilt', orbit: 'Orbit', handheld: 'Handheld',
};
const PITCH_WORDS: Record<string, string> = { level: 'Eye level', high: 'From above', low: 'From below' };

/**
 * THE CRAFT LINE under a beat — how it is shot and what it sounds like.
 *
 * Quiet on purpose. The action and the dialogue are what a creator reads; coverage is what they GLANCE at to
 * check the sequence is not four identical shots in a row. Rendered as words, never as the enum the render
 * stores.
 */
const CraftLine: React.FC<{ shot: ShotDraft }> = ({ shot }) => {
  const bits = [
    shot.camera?.shot_size ? SIZE_WORDS[shot.camera.shot_size] : '',
    shot.camera?.move ? MOVE_WORDS[shot.camera.move] : '',
    shot.camera?.pitch ? PITCH_WORDS[shot.camera.pitch] : '',
    shot.sfx || '',
  ].filter(Boolean);
  if (!bits.length) return null;
  return (
    <span className={`mt-0.5 block ${TYPE.meta} text-secondary-400 dark:text-secondary-500`} data-testid="beat-craft">
      {bits.join(' · ')}
    </span>
  );
};

/**
 * SPOKEN LINES, set the way a script sets them: the speaker's name above their words, indented.
 *
 * This is the whole reason dialogue is structured rather than folded into the action (owner 2026-08-30: *"the
 * writer's draft missing music, dialog, camera angle"*). Read as prose it is a synopsis; read like this it is
 * a script, and the same structure is what lets the line be SPOKEN in the character's cast voice instead of
 * becoming more image prompt.
 */
const DialogueLines: React.FC<{ shot: ShotDraft }> = ({ shot }) => {
  if (!shot.dialogue?.length) return null;
  return (
    <span className="mt-1.5 block space-y-1" data-testid="beat-dialogue">
      {shot.dialogue.map((d, i) => (
        <span key={i} className="block pl-3">
          {/* NARRATION is marked, because it plays differently: a spoken line is lip-synced to a character,
              a narrated one is voiceover with no forced mouth. Unlabelled, a creator cannot tell which they
              are reading — and the two produce very different clips. */}
          {(d.character || d.delivery === 'narration') && (
            <span className={`block ${TYPE.micro} font-semibold uppercase tracking-wide text-secondary-500 dark:text-secondary-400`}>
              {d.character || 'Narration'}
              {d.character && d.delivery === 'narration' ? ' (voiceover)' : ''}
            </span>
          )}
          <span className={`block ${TYPE.dialogue} text-secondary-700 dark:text-secondary-200`}>
            {d.line}
          </span>
        </span>
      ))}
    </span>
  );
};

/**
 * A beat line that grows with what is typed. The textarea has no chrome of its own so the script reads as
 * prose rather than as a form — the editing affordance is that the cursor lands in it, which is exactly how
 * the document editors this pattern comes from behave.
 *
 * The growing itself now comes from the shared `AutoTextarea` (owner 2026-08-24 asked for the same behaviour
 * on the project fields, and a second copy of a hand-rolled auto-resize is exactly how two surfaces drift).
 */
const BeatLine: React.FC<{ value: string; onChange?: (v: string) => void; index: number }> = ({ value, onChange, index }) => {
  if (!onChange) {
    return (
      <span className={`block ${TYPE.manuscript} text-secondary-900 dark:text-secondary-100`}>{value}</span>
    );
  }
  return (
    <AutoTextarea
      value={value}
      onChange={onChange}
      minRows={1}
      aria-label={`Beat ${index + 1}`}
      data-testid={`beat-${index}`}
      className={`block w-full border-0 bg-transparent p-0 ${TYPE.manuscript} text-secondary-900
                 outline-none focus:ring-0 dark:text-secondary-200`}
    />
  );
};

const ScriptPreview: React.FC<ScriptPreviewProps> = ({ draft, approved, drafting, onApprove, onRegenerate, onEditBeat }) => {
  // Before the first pass there is no document yet — and an empty card is not a document.
  if (!draft && !drafting) return null;

  if (drafting) {
    return (
      <section aria-label="Your writer is working" className="mt-4 flex items-center gap-2 py-6">
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-secondary-400" />
        <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>Your writer is developing the story…</p>
      </section>
    );
  }

  const beats = draft || [];
  return (
    <section aria-label={approved ? 'Your approved script' : "The writer's draft"} className="mt-5">
      {/* The document's own header line: what this is, how long it is, and the two things you can do to it. */}
      <div className="flex items-center gap-2 border-b border-secondary-200 pb-1.5 dark:border-secondary-600">
        <h3 className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>
          {approved ? 'Your script' : "The writer's draft"}
        </h3>
        <span className={`font-mono ${TYPE.meta} text-secondary-400`}>{beats.length} beats</span>
        <Tooltip content="Write it again from your idea">
          <button
            type="button"
            data-testid="regenerate-script"
            onClick={onRegenerate}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-2 py-1 ${TYPE.control} text-secondary-500
                       transition-colors hover:bg-secondary-100 hover:text-secondary-800 dark:text-secondary-400
                       dark:hover:bg-secondary-800 dark:hover:text-secondary-100`}
          >
            <RefreshCw className="h-3.5 w-3.5" /> Another pass
          </button>
        </Tooltip>
      </div>

      <ol className="space-y-2.5 py-3" data-testid="script-draft">
        {beats.map((d, i) => (
          <li key={i} className="flex gap-2.5">
            <span className={`mt-0.5 shrink-0 font-mono ${TYPE.meta} text-secondary-400`}>{i + 1}</span>
            <span className="min-w-0 flex-1">
              {d.beat && (
                <span className={`block ${TYPE.label} text-secondary-400`}>
                  {beatLabel(d.beat)}
                </span>
              )}
              <BeatLine
                index={i}
                value={d.line}
                onChange={onEditBeat ? (v) => onEditBeat(i, v) : undefined}
              />
              {/* What the writer decided BEYOND the action: who speaks, how it is covered, what it sounds
                  like. Without these on screen the draft reads as four lines of synopsis, which is exactly
                  what it was before the writer started producing them. */}
              <DialogueLines shot={d} />
              <CraftLine shot={d} />
            </span>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        {approved ? (
          /* Already committed: say so, quietly and once. Changing it is another pass or an edit above, not a
             second approval of the same thing. */
          <span
            data-testid="script-approved"
            className={`inline-flex items-center gap-1.5 ${TYPE.control} font-medium text-secondary-600 dark:text-secondary-300`}
          >
            <Check className="h-3.5 w-3.5" /> Approved — these beats are your shot list
          </span>
        ) : (
          <button
            type="button"
            data-testid="approve-script"
            onClick={onApprove}
            className={`inline-flex min-h-[34px] items-center gap-2 rounded-lg bg-secondary-900 px-3.5 ${TYPE.control}
                       font-semibold text-white transition-opacity hover:opacity-90 dark:bg-white dark:text-secondary-900`}
          >
            <Check className="h-3.5 w-3.5" /> Approve script
          </button>
        )}
        <span className={`${TYPE.meta} text-secondary-400`}>
          {onEditBeat ? 'Edit any beat directly, or ask for another pass.' : 'Ask for another pass to rewrite it.'}
        </span>
      </div>
    </section>
  );
};



/** A beat's kind in sentence case ("HOOK" -> "Hook"): capitals read as a technical tag to a writer (TYPE-2). */
export function beatLabel(beat: string): string {
  const b = String(beat || '').trim().toLowerCase();
  return b ? `${b.charAt(0).toUpperCase()}${b.slice(1)}` : '';
}
export default ScriptPreview;
